import { useState, useEffect, useRef } from 'react';
import { supabase } from '../lib/supabaseClient';

// Generalized version of getCleanLogoUrl
export const getCleanImageUrl = (imageUrl, backendDefaultPath, frontendFallback) => {
  if (!imageUrl || imageUrl.trim() === "" || imageUrl === "/uploads/teams/default-team-logo.png" || imageUrl === "/uploads/avatars/default-user-avatar.png") {
    // If imageUrl is empty, null, or a placeholder, use the canonical backend default
    return backendDefaultPath;
  }
  
  // If it's already our frontend fallback, return as-is
  if (imageUrl === frontendFallback) {
    return imageUrl;
  }
  
  // Allow previews generated via URL.createObjectURL (blob: scheme)
  if (imageUrl.startsWith('blob:')) {
    return imageUrl;
  }

  // Allow previews generated via FileReader (data: scheme)
  if (imageUrl.startsWith('data:')) {
    return imageUrl;
  }
  
  // Handle malformed URLs that start with /https:// or /http://
  if (imageUrl.startsWith('/https://') || imageUrl.startsWith('/http://')) {
    return imageUrl.substring(1); // Remove the leading slash
  }
  
  // Handle regular absolute URLs
  if (imageUrl.startsWith('https://') || imageUrl.startsWith('http://')) {
    return imageUrl;
  }
  
  // Handle valid relative paths from backend
  if (imageUrl.startsWith('/uploads/')) {
    return imageUrl;
  }

  // Handle built frontend assets emitted by Vite
  if (imageUrl.startsWith('/assets/')) {
    return imageUrl;
  }
  
  console.warn(`[getCleanImageUrl] Unrecognized image URL format: "${imageUrl}". Falling back to backend default.`);
  return backendDefaultPath;
};

// Resolve backend-served assets (e.g. /uploads/...) to an absolute URL when VITE_API_URL is absolute.
// If VITE_API_URL is relative (e.g. "/api"), keep the path relative so dev/prod reverse proxies can handle it.
export const resolveBackendAssetUrl = (assetUrl, apiBaseUrl = import.meta.env.VITE_API_URL) => {
  if (!assetUrl) return assetUrl;

  if (typeof assetUrl !== 'string') return assetUrl;

  const normalizedAssetUrl = assetUrl.trim();
  if (!normalizedAssetUrl) return normalizedAssetUrl;

  const lowerAssetUrl = normalizedAssetUrl.toLowerCase();
  if (lowerAssetUrl.startsWith('/http://') || lowerAssetUrl.startsWith('/https://')) {
    return normalizedAssetUrl.substring(1);
  }

  if (
    lowerAssetUrl.startsWith('http://') ||
    lowerAssetUrl.startsWith('https://') ||
    lowerAssetUrl.startsWith('blob:') ||
    lowerAssetUrl.startsWith('data:')
  ) {
    return normalizedAssetUrl;
  }

  if (!normalizedAssetUrl.startsWith('/')) {
    return normalizedAssetUrl;
  }

  if (!apiBaseUrl) return normalizedAssetUrl;

  try {
    const origin = new URL(apiBaseUrl).origin;
    return `${origin}${normalizedAssetUrl}`;
  } catch (_) {
    // apiBaseUrl is likely relative (e.g. "/api") or otherwise not a valid absolute URL
    return normalizedAssetUrl;
  }
};

// Extract file path from Supabase URL
export const extractSupabaseFilePath = (url) => {
  if (!url || !url.includes('supabase.co')) return null;
  
  // Extract path from URLs like: https://xsrwligqdlmkyqdsczru.supabase.co/storage/v1/object/public/team-logos/67e9571ff02fc4024095e886/logo.jpg
  const match = url.match(/\/storage\/v1\/object\/public\/(.*)/);
  return match ? match[1] : null;
};

// Custom hook for Supabase image loading with authentication
export const useSupabaseImage = (imageUrl, imageName, frontendFallback, backendDefaultPath) => {
  const [currentSrc, setCurrentSrc] = useState(frontendFallback);
  const [hasErrored, setHasErrored] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const errorCountRef = useRef(0);
  const blobUrlRef = useRef(null);

  // Cleanup blob URLs on unmount
  useEffect(() => {
    return () => {
      if (blobUrlRef.current && blobUrlRef.current !== frontendFallback && blobUrlRef.current.startsWith('blob:')) {
        URL.revokeObjectURL(blobUrlRef.current);
      }
    };
  }, [frontendFallback]);

  // Load image when imageUrl changes
  useEffect(() => {
    setHasErrored(false);
    errorCountRef.current = 0;
    setIsLoading(false);

    const loadImage = async () => {
      if (!imageUrl) {
        setCurrentSrc(frontendFallback);
        return;
      }

      const cleanUrl = getCleanImageUrl(imageUrl, backendDefaultPath, frontendFallback);
      
      // If it's not a Supabase URL, use it directly
      if (!cleanUrl.includes('supabase.co')) {
        setCurrentSrc(cleanUrl);
        return;
      }

      // For Supabase URLs, use authenticated download
      const filePath = extractSupabaseFilePath(cleanUrl);
      if (!filePath) {
        console.warn(`Could not extract file path from Supabase URL: ${cleanUrl}`);
        setCurrentSrc(frontendFallback);
        return;
      }

      setIsLoading(true);
      
      try {
        // Determine bucket from file path
        const [bucketName, ...pathParts] = filePath.split('/');
        const fileName = pathParts.join('/');
        
        const { data, error } = await supabase.storage
          .from(bucketName)
          .download(fileName);
        
        if (error) {
          throw error;
        }
        
        // Cleanup previous blob URL
        if (blobUrlRef.current && blobUrlRef.current !== frontendFallback && blobUrlRef.current.startsWith('blob:')) {
          URL.revokeObjectURL(blobUrlRef.current);
        }
        
        // Create new blob URL
        const blobUrl = URL.createObjectURL(data);
        blobUrlRef.current = blobUrl;
        setCurrentSrc(blobUrl);
        setIsLoading(false);
        
      } catch (error) {
        console.log(`Error loading ${imageName} from Supabase:`, error.message);
        setCurrentSrc(frontendFallback);
        setHasErrored(true);
        setIsLoading(false);
      }
    };

    loadImage();
  }, [imageUrl, imageName, frontendFallback, backendDefaultPath]);

  const handleError = (e) => {
    errorCountRef.current += 1;
    
    // Prevent infinite loops - max 1 error attempt for authenticated loading
    if (errorCountRef.current > 1 || hasErrored) {
      console.warn(`Too many errors for ${imageName}, stopping retries`);
      return;
    }

    console.log(`Error loading ${imageName} (attempt ${errorCountRef.current}):`, e.target.src);
    
    // Set fallback and mark as errored
    setHasErrored(true);
    setCurrentSrc(frontendFallback);
  };

  return {
    currentSrc,
    isLoading,
    hasErrored,
    handleError
  };
}; 
